import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, Menu, MenuItem, ListItemIcon, ListItemText, IconButton, Tooltip, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Category } from '../../types/Category';
import { Content } from '../../types/Content';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import EditIcon from '@mui/icons-material/Edit';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import { useHelpContent } from '../../utils/HelpUtils';

interface CategoryContentProps {
  categories: Category[];
  selectedCategory: Category | null;
  onCategoryMultiSelect: (category: Category, event: React.MouseEvent) => void;
  onContentMultiSelect: (content: Content, event: React.MouseEvent) => void;
  selectedCategories: Category[];
  selectedContents: Content[];
  onSelectAllCategories: (checked: boolean) => void;
  onSelectAllContents: (checked: boolean, contentsScope?: Content[]) => void;
  onEditContent?: () => void;
  onGroupCategories?: () => void;
  onMergeCategories?: () => void;
  onDeleteSelected?: () => void;
  onMoveSelected?: (direction: 'up' | 'down') => void;
  onCutSelected?: () => void;
  onCopySelected?: () => void;
  canEditContent?: boolean;
  canGroupCategories?: boolean;
  canMergeCategories?: boolean;
  canDeleteSelected?: boolean;
  canMoveSelected?: (direction: 'up' | 'down') => boolean;
  canCutSelected?: boolean;
  canCopySelected?: boolean;
  onDropCategories?: (categories: Category[], targetCategory: Category, position: 'before' | 'after' | 'inside') => void;
  onDropContents?: (contents: Content[], targetContent: Content, position: 'before' | 'after' | 'inside') => void;
  matchingContentIds?: string[];
  /** When non-null, only contents whose `subcase` is in this list are shown in the content table. */
  visibleSubcaseFilter?: string[] | null;
  /** When true, content cannot be reordered by dropping on other content rows; dragging to a category in the tree still works. */
  contentReorderDisabled?: boolean;
}

const ContentContainer = styled(Box)({
  padding: '16px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const StyledTable = styled(Table)({
  fontSize: 'var(--app-font-size, 14px)',
  '& .MuiTableCell-root': {
    padding: '4px 8px',
    fontSize: 'var(--app-font-size, 14px)',
  },
  '& .MuiTableRow-root': {
    height: '32px',
  },
  '& .MuiTableRow-root:hover': {
    cursor: 'grab',
  },
  '& .MuiTableRow-root:active': {
    cursor: 'grabbing',
  },
});

const ResizeHandle = styled('div')({
  width: '4px',
  height: '100%',
  cursor: 'col-resize',
  position: 'absolute',
  right: 0,
  top: 0,
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: '#1976d2',
  },
});

const ResizableTableCell = styled(TableCell)({
  position: 'relative',
  padding: '4px 8px',
  '&:hover .resize-handle': {
    backgroundColor: '#1976d2',
  },
});

const isContent = (item: Category | Content): item is Content => {
  return 'subcase' in item && 'text' in item;
};

const CategoryContent = forwardRef<HTMLDivElement, CategoryContentProps>(({
  categories,
  selectedCategory,
  onCategoryMultiSelect,
  onContentMultiSelect,
  selectedCategories,
  selectedContents,
  onSelectAllCategories,
  onSelectAllContents,
  onEditContent,
  onGroupCategories,
  onMergeCategories,
  onDeleteSelected,
  onMoveSelected,
  onCutSelected,
  onCopySelected,
  canEditContent = false,
  canGroupCategories = false,
  canMergeCategories = false,
  canDeleteSelected = false,
  canMoveSelected = () => false,
  canCutSelected = false,
  canCopySelected = false,
  onDropCategories,
  onDropContents,
  matchingContentIds = [],
  visibleSubcaseFilter = null,
  contentReorderDisabled = false,
}, ref) => {
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({
    checkbox: 5,
    type: 5,
    name: 100,
    description: 200,
    subcase: 100,
    content: 400,
  });

  const resizeRef = useRef<{
    columnId: string | null;
    startX: number;
    startWidth: number;
  }>({
    columnId: null,
    startX: 0,
    startWidth: 0,
  });

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    type: 'category' | 'content';
    item: Category | Content;
  } | null>(null);

  const contentContainerRef = useRef<HTMLDivElement>(null);
  const { getMenuTooltipTextForState } = useHelpContent();

  // Expose the ref to the parent component
  useImperativeHandle(ref, () => contentContainerRef.current!);

  const handleResizeStart = (e: React.MouseEvent, columnId: string) => {
    resizeRef.current = {
      columnId,
      startX: e.clientX,
      startWidth: columnWidths[columnId],
    };
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (resizeRef.current.columnId) {
      const { columnId, startX, startWidth } = resizeRef.current;
      const newWidth = startWidth + (e.clientX - startX);
      setColumnWidths(prev => ({
        ...prev,
        [columnId]: Math.max(48, newWidth),
      }));
    }
  };

  const handleResizeEnd = () => {
    resizeRef.current.columnId = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };

  const handleContextMenu = (event: React.MouseEvent, type: 'category' | 'content', item: Category | Content) => {
    event.preventDefault();
    event.stopPropagation();
    
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      type,
      item,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  if (!selectedCategory) {
    return (
      <ContentContainer ref={contentContainerRef}>
        <Typography variant="h6">Select a category to view its contents</Typography>
      </ContentContainer>
    );
  }

  const handleCategoryClick = (event: React.MouseEvent, category: Category) => {
    onCategoryMultiSelect(category, event);
  };

  const handleContentClick = (event: React.MouseEvent, content: Content) => {
    onContentMultiSelect(content, event);
  };

  const isCategorySelected = (category: Category) => {
    return selectedCategories.some(c => c.id === category.id);
  };

  const isContentSelected = (content: Content) => {
    return selectedContents.some(c => c.id === content.id);
  };

  const handleSelectAllCategories = (event: React.ChangeEvent<HTMLInputElement>) => {
     onSelectAllCategories(event.target.checked);
   };

type DraggableContentRowProps = {
  content: Content;
  onClick: (event: React.MouseEvent, content: any) => void;
  onContextMenu: (event: React.MouseEvent, content: any) => void;
  isSelected: boolean;
  selectedContents: Content[];
  matchingContentIds?: string[];
  reorderDisabled?: boolean;
};

const findCategoryById = (categories: Category[], targetId: string): Category | null => {
    // First check root level
    const category = categories.find(cat => cat.id === targetId);
    if (category) return category;

    // Then check each category's children
    for (const parent of categories) {
      if (parent.children) {
        const found = findCategoryById(parent.children, targetId);
        if (found) return found;
      }
    }

    return null;
  };

const findContentById = (cats: Category[], id: string): Content | null => {
  if (!cats || !Array.isArray(cats) || !id) return null;

    for (const cat of cats) {
      if (cat.contentCategory) {
        if ( cat.contents) {
            const content = cat.contents.find(c => c.id === id);
            if ( content) return content;
        }
      }
      else {
          if (cat.children) {
            const found = findContentById(cat.children, id);
            if (found) return found;
          }
      }
    }
    return null;
  };


const DraggableContentRow: React.FC<DraggableContentRowProps> = ({
  content,
  onClick,
  onContextMenu,
  isSelected,
  selectedContents,
  matchingContentIds = [],
  reorderDisabled = false,
}) => {
  type ContentsDragItem = {
    type: 'CONTENT_ROW';
    contents: Content[];
  };

  const ref = useRef<HTMLDivElement | null>(null);

  const [dropTarget, setDropTarget] = useState<{
    contentId: string;
    position: 'before' | 'after' | 'inside';
  } | null>(null);

  const [{ isDragging }, drag] = useDrag(() => ({
      type: 'CONTENT_ROW',
      // Always allow dragging selected rows so content can be dropped on categories in the tree;
      // reorderDisabled only turns off useDrop between content rows below.
      canDrag: () =>
        selectedContents.length > 0 &&
        selectedContents.some(c => c.id === content.id),
      item: () => {
          return {type: 'CONTENT_ROW', contents: selectedContents};
        },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }), [selectedContents]);

  const [{ isOver, itemType }, drop] = useDrop<ContentsDragItem, void, { isOver: boolean; itemType: string | null }>({
    accept: 'CONTENT_ROW',
    canDrop: () => !reorderDisabled,
    drop: (item: { contents: Content[] }, monitor) => {
        if (reorderDisabled || !dropTarget || item.contents.some(c => c.id === content.id)) return;

        const targetContent = findContentById(categories, dropTarget.contentId);
        if ( targetContent)
            onDropContents?.(item.contents, targetContent, dropTarget.position);

    },
     hover(item: ContentsDragItem, monitor: DropTargetMonitor<ContentsDragItem, void>) {
       if (reorderDisabled) {
         setDropTarget(null);
         return;
       }
       if (!ref.current) return;

         const isOver = monitor.isOver({ shallow: true });
         if (!isOver) {
           setDropTarget(null);
           return;
         }

       const clientOffset = monitor.getClientOffset();
       const boundingRect = ref.current?.getBoundingClientRect();
       if (!clientOffset || !boundingRect) return;

       const y = clientOffset.y - boundingRect.top;
       const height = boundingRect.height;

       let position: 'before' | 'after' | 'inside' = 'inside';
       if (y < height / 3) position = 'before';
       else if (y > (2 * height) / 3) position = 'after';

       setDropTarget({ contentId: content.id, position });
     },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      itemType: monitor.getItemType() as string | null,
    }),
  });

  useEffect(() => {
       if (!isOver) {
         setDropTarget((prev) => {
           // Only clear if this item owns the current dropTarget
           if (prev?.contentId === content.id) {
             return null;
           }
           return prev;
         });
       }
     }, [isOver, content.id]);

  return (
    <TableRow
        ref={(node: HTMLDivElement | null) => {
             ref.current = node; // This assigns the ref
             if (node) drag(drop(node)); // Attach DnD
         }}
       sx={{
            opacity: isDragging ? 0.5 : 1,
            //backgroundColor: isSelected ? 'lightblue' : undefined,
            //display: 'flex',
            alignItems: 'center',
            width: '100%',
            cursor: isSelected ? 'grab' : reorderDisabled ? 'default' : 'move',
            '&:active': { cursor: isSelected ? 'grabbing' : reorderDisabled ? 'default' : 'grabbing' },
            padding: '4px 8px',
            borderRadius: '4px',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            borderTop:
              dropTarget?.contentId === content.id && dropTarget?.position === 'before'
                ? '2px solid #64b5f6'
                : undefined,
            borderBottom:
              dropTarget?.contentId === content.id && dropTarget?.position === 'after'
                ? '2px solid #64b5f6'
                : undefined,
            backgroundColor:
              dropTarget?.contentId === content.id && dropTarget?.position === 'inside'
                ? 'rgba(25, 118, 210, 0.1)'
                : matchingContentIds.includes(content.id)
                ? 'rgba(255, 255, 0, 0.4)' // Yellow highlight for search matches
                : undefined,
            border:
              matchingContentIds.includes(content.id)
                ? '2px solid #ffd700' // Gold border for search matches
                : undefined,
          }}
      key={content.id}
      hover
      onClick={(e) => onClick(e, content)}
      selected={isSelected}
      onContextMenu={(e) => onContextMenu(e, content)}
    >
      <TableCell padding="checkbox">
        <Checkbox
          size="small"
          checked={isContentSelected(content)}
          onClick={(event) => {
            event.stopPropagation();
            handleContentClick(event, content);
          }}
        />
      </TableCell>
      <TableCell>{<StickyNote2OutlinedIcon sx={{ color: '#2c3e50', mr: 1 }}/>}</TableCell>
      <TableCell>{content.subcase}</TableCell>
      <TableCell>
        {content.text.length > 100 ? `${content.text.substring(0, 100)}...` : content.text}
      </TableCell>
    </TableRow>
  );
}

type DraggableCategoryRowProps = {
    category: Category;
    onClick: (event: React.MouseEvent, content: any) => void;
    onContextMenu: (event: React.MouseEvent, content: any) => void;
    isSelected: boolean;
    selectedCategories: Category[];
  };

  const DraggableCategoryRow: React.FC<DraggableCategoryRowProps> = ({
    category,
    onClick,
    onContextMenu,
    isSelected,
  }) => {

   const ref = useRef<HTMLDivElement | null>(null);

  const [dropTarget, setDropTarget] = useState<{
    categoryId: string;
    position: 'before' | 'after' | 'inside';
  } | null>(null);


   type CategoriesDragItem = {
       type: 'CATEGORY_ROW';
       categories: Category[];
     };

    const [{ isDragging }, drag] = useDrag(() => ({
          type: 'CATEGORY_ROW',
          canDrag: () => {return selectedCategories.length > 0 && selectedCategories.some(c => c.id === category.id)},
          item: () => {
              return {type: 'CATEGORY_ROW', categories: selectedCategories};
            },
          collect: (monitor) => ({
            isDragging: monitor.isDragging(),
          }),
        }), [selectedCategories]);

      const [{ isOver, itemType }, drop] = useDrop<CategoriesDragItem, void, { isOver: boolean; itemType: string | null }>({
        accept: 'CATEGORY_ROW',
        drop: (item: { categories: Category[] }, monitor) => {
            if (!dropTarget || item.categories.some(c => c.id === category.id)) return;

            const targetCategory = findCategoryById(categories, dropTarget.categoryId);
            if ( targetCategory)
                onDropCategories?.(item.categories, targetCategory, dropTarget.position);

        },
         hover(item: CategoriesDragItem, monitor: DropTargetMonitor<CategoriesDragItem, void>) {
           if (!ref.current) return;

             const isOver = monitor.isOver({ shallow: true });
             if (!isOver) {
               setDropTarget(null);
               return;
             }

           const clientOffset = monitor.getClientOffset();
           const boundingRect = ref.current?.getBoundingClientRect();
           if (!clientOffset || !boundingRect) return;

           const y = clientOffset.y - boundingRect.top;
           const height = boundingRect.height;

           let position: 'before' | 'after' | 'inside' = 'inside';
           if (y < height / 3) position = 'before';
           else if (y > (2 * height) / 3) position = 'after';

           setDropTarget({ categoryId: category.id, position });
         },
        collect: (monitor) => ({
          isOver: monitor.isOver(),
          itemType: monitor.getItemType() as string | null,
        }),
      });

      useEffect(() => {
           if (!isOver) {
             setDropTarget((prev) => {
               // Only clear if this item owns the current dropTarget
               if (prev?.categoryId === category.id) {
                 return null;
               }
               return prev;
             });
           }
         }, [isOver, category.id]);


    return (
     <TableRow
       ref={(node: HTMLDivElement | null) => {
            ref.current = node; // This assigns the ref
            if (node) drag(drop(node)); // Attach DnD
        }}
          sx={{
               opacity: isDragging ? 0.5 : 1,
               //backgroundColor: isSelected ? 'lightblue' : undefined,
               //display: 'flex',
               alignItems: 'center',
               width: '100%',
               cursor:  isSelected ? 'grab' : 'move',
               '&:active': { cursor: 'grabbing' },
               padding: '4px 8px',
               borderRadius: '4px',
               '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
               borderTop:
                 dropTarget?.categoryId === category.id && dropTarget?.position === 'before'
                   ? '2px solid #64b5f6'
                   : undefined,
               borderBottom:
                 dropTarget?.categoryId === category.id && dropTarget?.position === 'after'
                   ? '2px solid #64b5f6'
                   : undefined,
               backgroundColor:
                 dropTarget?.categoryId === category.id && dropTarget?.position === 'inside'
                   ? 'rgba(25, 118, 210, 0.1)'
                   : undefined,
             }}
         key={category.id}
         hover
         onClick={(e) => onClick(e, category)}
         selected={isSelected}
         onContextMenu={(e) => onContextMenu(e, category)}
       >
       <TableCell padding="checkbox">
         <Checkbox
           checked={isCategorySelected(category)}
           onClick={(event) => {
             event.stopPropagation();
             handleCategoryClick(event, category);
           }}
               />
             </TableCell>
       <TableCell>{category.contentCategory ? <DescriptionIcon sx={{ color: '#2c3e50', mr: 1 }}/> : <FolderIcon sx={{ color: '#2c3e50', mr: 1 }} />}</TableCell>
       <TableCell>{category.name}</TableCell>
       <TableCell>{category.description}</TableCell>
     </TableRow>
   );
  }

  if (selectedCategory.contentCategory) {
    const contentsInView =
      visibleSubcaseFilter != null && selectedCategory.contents
        ? selectedCategory.contents.filter(c => visibleSubcaseFilter.includes(c.subcase))
        : selectedCategory.contents ?? [];

    const visibleSelectedCount = selectedContents.filter(c =>
      contentsInView.some(v => v.id === c.id)
    ).length;

    const handleSelectAllContentsInView = (event: React.ChangeEvent<HTMLInputElement>) => {
      onSelectAllContents(event.target.checked, contentsInView);
    };

  return (
      <ContentContainer ref={contentContainerRef}>
        <TableContainer component={Paper}>
          <StyledTable size="small">
            <TableHead>
              <TableRow>
              <ResizableTableCell style={{ width: columnWidths.checkbox }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      size="small"
                      indeterminate={
                        visibleSelectedCount > 0 &&
                        contentsInView.length > 0 &&
                        visibleSelectedCount < contentsInView.length
                      }
                      checked={
                        contentsInView.length > 0 &&
                        visibleSelectedCount === contentsInView.length
                      }
                      onChange={handleSelectAllContentsInView}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {visibleSelectedCount} / {contentsInView.length}
                    </Typography>
                  </Box>
                </ResizableTableCell>
              <TableCell style={{ width: columnWidths.type }}></TableCell>
              <ResizableTableCell style={{ width: columnWidths.subcase }}>
                Subcase
                <ResizeHandle
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart(e, 'subcase')}
                />
              </ResizableTableCell>
              <ResizableTableCell style={{ width: columnWidths.content }}>
                Content
                <ResizeHandle
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart(e, 'content')}
                />
              </ResizableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {contentsInView.map((content) => (
               <DraggableContentRow
                  key={content.id}
                  content={content}
                  onClick={(event) => handleContentClick(event, content)}
                  onContextMenu={(event) => handleContextMenu(event, 'content', content)}
                  isSelected={isContentSelected(content)}
                  selectedContents={selectedContents}
                  matchingContentIds={matchingContentIds}
                  reorderDisabled={contentReorderDisabled}
                />
            ))}
          </TableBody>
        </StyledTable>
        <Menu
          open={contextMenu?.type === 'content' ? true : false}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <Tooltip title={getMenuTooltipTextForState('edit-content', !!(selectedContents.length > 0 && canEditContent))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onEditContent?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedContents.length === 0 || !canEditContent}
              >
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit Content</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('delete-content', !!(selectedContents.length > 0 && canDeleteSelected))} placement="right">
          <span>
            <MenuItem
              onClick={() => {
                onDeleteSelected?.();
                handleCloseContextMenu();
              }}
              disabled={selectedContents.length === 0 || !canDeleteSelected}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete Content</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('move-up', !!(selectedContents.length > 0 && canMoveSelected('up')))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onMoveSelected?.('up');
                  handleCloseContextMenu();
                }}
                disabled={selectedContents.length === 0 || !canMoveSelected('up')}
              >
                <ListItemIcon>
                  <ArrowUpwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Move Up</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('move-down', !!(selectedContents.length > 0 && canMoveSelected('down')))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onMoveSelected?.('down');
                  handleCloseContextMenu();
                }}
                disabled={selectedContents.length === 0 || !canMoveSelected('down')}
              >
                <ListItemIcon>
                  <ArrowDownwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Move Down</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('cut', !!(selectedContents.length > 0 && canCutSelected))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onCutSelected?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedContents.length === 0 || !canCutSelected}
              >
                <ListItemIcon>
                  <ContentCutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Cut</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('copy', !!(selectedContents.length > 0 && canCopySelected))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onCopySelected?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedContents.length === 0 || !canCopySelected}
              >
                <ListItemIcon>
                  <ContentCopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Copy</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        </Menu>
        </TableContainer>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer ref={contentContainerRef}>
      <TableContainer component={Paper}>
                    <StyledTable className="table-fixed w-full border" size="small">
          <TableHead>
            <TableRow>
              <TableCell className="w-12" style={{ width: columnWidths.checkbox }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    size="small"
                    indeterminate={
                      selectedCategories.length > 0 &&
                      selectedCategories.length < (selectedCategory.children?.length || 0)
                    }
                    checked={
                      selectedCategory.children &&
                      selectedCategories.length === selectedCategory.children.length
                    }
                    onChange={handleSelectAllCategories}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {selectedCategories.length} / {selectedCategory.children?.length || 0}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell style={{ width: columnWidths.type }}>Type</TableCell>
              <ResizableTableCell style={{ width: columnWidths.name }}>
                Name
                <ResizeHandle
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart(e, 'name')}
                />
              </ResizableTableCell>
              <ResizableTableCell style={{ width: columnWidths.description }}>
                Description
                <ResizeHandle
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart(e, 'description')}
                />
              </ResizableTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedCategory.children?.map((category) => (
              <DraggableCategoryRow
                key={category.id}
                category={category}
                onClick={(event) => handleCategoryClick(event, category)}
                onContextMenu={(event) => handleContextMenu(event, 'category', category)}
                isSelected={isCategorySelected(category)}
                selectedCategories={selectedCategories}
              />
            ))}
          </TableBody>
        </StyledTable>
        <Menu
          open={contextMenu?.type === 'category' ? true : false}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <Tooltip title={getMenuTooltipTextForState('group-categories', !!(selectedCategories.length > 0 && canGroupCategories))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onGroupCategories?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedCategories.length === 0 || !canGroupCategories}
              >
                <ListItemIcon>
                  <CallMergeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Group Categories</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('merge-categories', !!(selectedCategories.length > 0 && canMergeCategories))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  onMergeCategories?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedCategories.length === 0 || !canMergeCategories}
              >
                <ListItemIcon>
                  <CallMergeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Merge Categories</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('move-up', !!(selectedCategories.length > 0 && canMoveSelected('up')))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onMoveSelected?.('up');
                  handleCloseContextMenu();
                }}
                disabled={selectedCategories.length === 0 || !canMoveSelected('up')}
              >
                <ListItemIcon>
                  <ArrowUpwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Move Up</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('move-down', !!(selectedCategories.length > 0 && canMoveSelected('down')))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onMoveSelected?.('down');
                  handleCloseContextMenu();
                }}
                disabled={selectedCategories.length === 0 || !canMoveSelected('down')}
              >
                <ListItemIcon>
                  <ArrowDownwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Move Down</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('cut', !!(selectedCategories.length > 0 && canCutSelected))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  onCutSelected?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedCategories.length === 0 || !canCutSelected}
              >
                <ListItemIcon>
                  <ContentCutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Cut</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('copy', !!(selectedCategories.length > 0 && canCopySelected))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onCopySelected?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedCategories.length === 0 || !canCopySelected}
              >
                <ListItemIcon>
                  <ContentCopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Copy</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        </Menu>
        </TableContainer>
    </ContentContainer>
  );
});


export default CategoryContent; 